// Images above these limits are downscaled before being embedded as Base64 in the email body.
export const MAX_IMAGE_WIDTH = 1600;
export const MAX_IMAGE_BYTES = 1024 * 1024;

const RESIZABLE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/bmp'];
const JPEG_QUALITY = 0.85;

export const shouldResizeImage = file =>
  !!file &&
  RESIZABLE_TYPES.includes(file.type) &&
  file.size > MAX_IMAGE_BYTES;

export const blobToDataUrl = blob =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const canvasToBlob = (canvas, type, quality) =>
  canvas.convertToBlob
    ? canvas.convertToBlob({ type, quality })
    : new Promise(resolve => canvas.toBlob(resolve, type, quality));

/**
 * Decodes and downscales the image without blocking the main thread (createImageBitmap decodes
 * asynchronously) and resolves with a Base64 data URL. PNG stays PNG so transparency is kept;
 * other formats are re-encoded as JPEG.
 *
 * Resolves with null when the image does not need resizing so the caller can fall back to
 * the original file.
 */
export async function resizeImageToDataUrl(file, maxWidth = MAX_IMAGE_WIDTH) {
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, maxWidth / bitmap.width);
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(width, height)
        : Object.assign(document.createElement('canvas'), { width, height });
    const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
    const context = canvas.getContext('2d');
    if (outputType === 'image/jpeg') {
      // JPEG has no alpha channel: avoid transparent areas turning black
      context.fillStyle = '#fff';
      context.fillRect(0, 0, width, height);
    }
    context.drawImage(bitmap, 0, 0, width, height);
    const blob = await canvasToBlob(canvas, outputType, JPEG_QUALITY);
    if (!blob || blob.size >= file.size) {
      return null;
    }
    return blobToDataUrl(blob);
  } finally {
    bitmap.close();
  }
}
